// SurveyDataContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

// Action (from the snippet)
import {
  SG_FETCH_TASK_SCHEDULE,
  SG_ADD_USER_TASK,
  SG_DELETE_USER_TASK,
  SG_UPDATE_EMAIL_TEMPLATE,
  SG_ADD_EMPLOYEE_REPORT_ACCESS,
  SG_REMOVE_EMPLOYEE_REPORT_ACCESS,
  SG_UPDATE_PULSE,
  SG_UPDATE_TASK_TEMPLATE,
} from "constants/actions";

const normalizeEmail = (v) =>
  (typeof v === "string" ? v.trim().toLowerCase() : "");

const buildEmployeeIndexes = (employees) => {
  const byId = new Map();
  const byEmail = new Map();
  (employees || []).forEach((e) => {
    if (e?.id != null) byId.set(Number(e.id), e);
    const em = normalizeEmail(e?.email);
    if (em) byEmail.set(em, e);
  });
  return { byId, byEmail };
};

const mapRecipientsToApi = (arr, employees) => {
  const { byId, byEmail } = buildEmployeeIndexes(employees);
  const idList = [];
  const emailList = [];

  (arr || []).forEach((val) => {
    const asNumber = Number(val);
    if (!Number.isNaN(asNumber) && byId.has(asNumber)) {
      idList.push(asNumber);
      return;
    }
    const em = normalizeEmail(val);
    if (em && byEmail.has(em)) {
      idList.push(Number(byEmail.get(em).id));
      return;
    }
    if (em) emailList.push(em);
  });

  return { idList, emailList };
};

// Utils
const getRecentResponses = (data, employees, filters, get_employee_categories) => {
  const responses = {};
  const employeeList = employees?.employees;
  const activeFilters = Array.isArray(filters) ? filters : [];
  const employeeCategories = Array.isArray(get_employee_categories)
    ? get_employee_categories
    : [];
  const taskResponses = Array.isArray(data?.task_responses)
    ? data.task_responses
    : Object.values(data?.task_responses || {});

  const filteredEmployeeCategories = employeeCategories
    .filter((employeeCategory) => {
      if (activeFilters.length === 0) {
        return true;
      }

      return activeFilters.every((filter) => {
        const matchingCategory = employeeCategory.category.find(
          (cat) => cat.value_id === filter.id
        );
        return !!matchingCategory;
      });
    })
    .map((employeeCategory) => employeeCategory.employee_id);

  taskResponses.forEach((response) => {
    const employeeId = Number(response.employee);
    // Find the employee's name, and add it to the response object under the key "employee_full_name"
    // Keep the most recent response for each employee
    const employeeObj = employeeList?.find((emp) => emp.id === employeeId);
    response.employee_full_name = `${employeeObj?.first_name} ${employeeObj?.last_name}`;
    if (
      !responses[employeeId] ||
      response.created_at > responses[employeeId].created_at
    ) {
      responses[employeeId] = response;
    }
  });

  // Filter the responses based on the filteredEmployeeCategories
  const filteredResponses = Object.values(responses).filter((response) => {
    if (activeFilters.length === 0) {
      return true;
    }

    // Check if the response.employee is in the filteredEmployeeCategories array
    return filteredEmployeeCategories.includes(response.employee);
  });

  return filteredResponses;
};

// Calculate compliance percentage
const calculateCompliance = (participants, responses) => {
  if (!participants || participants.length === 0) {
    return 0;
  }

  // Get unique participant IDs
  const participantIds = participants.map(p => p.id);

  // Get unique response employee IDs
  const responseEmployeeIds = responses.map(r => Number(r.employee));

  // Count how many participants have responded
  const respondedCount = participantIds.filter(id =>
    responseEmployeeIds.includes(id)
  ).length;

  // Calculate percentage
  const compliancePercentage = Math.round((respondedCount / participantIds.length) * 100);

  return {
    percentage: compliancePercentage,
    respondedCount,
    totalParticipants: participantIds.length
  };
};

// Create the context
const SurveyDataContext = createContext(null);

// Custom Hook to use the context
export const useSurveyDataContext = () => useContext(SurveyDataContext);

// Provider component
export const SurveyDataProvider = ({ children, surveyId: surveyIdProp }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams();
  const { addToast } = useToasts();

  const resolvedSurveyId = useMemo(() => {
    if (surveyIdProp) return String(surveyIdProp);
    if (paramId) return String(paramId);
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return "";
    const lastSegment = segments[segments.length - 1];
    return /^\d+$/.test(lastSegment) ? lastSegment : "";
  }, [surveyIdProp, paramId, location.pathname]);

  const numericSurveyId = resolvedSurveyId ? Number(resolvedSurveyId) : null;

  const [responses, setResponses] = useState([]);
  const [downloadableReports, setDownloadableReports] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSurveyCreator, setIsSurveyCreator] = useState(false);
  const [surveyParticipants, setSurveyParticipants] = useState([]);
  const [surveyIsAnonymous, setSurveyIsAnonymous] = useState(false);
  const [compliance, setCompliance] = useState({
    percentage: 0,
    respondedCount: 0,
    totalParticipants: 0
  });
  const [hasHydrated, setHasHydrated] = useState(false);
  const [reportAccess, setReportAccess] = useState([]);

  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [hasEmailChanged, setHasEmailChanged] = useState(false);
  const [pendingUserAssignment, setPendingUserAssignment] = useState(null);
  const [newEmailSubject, setNewEmailSubject] = useState("");
  const [newEmailBody, setNewEmailBody] = useState("");

  const [surveyInfo, setSurveyInfo] = useState({
    title: "",
    description: "",
    createdBy: "",
    createdDate: "",
    updatedDate: "",
    surveyIsAnonymous: false,
    surveyIsOpen: true,
    task_template_id: null,
  });

  // Redux Data
  const {
    fetched_scheduled_task,
    get_employees,
    get_auth,
    pendingReport,
    updated_email_template,
    filters,
    get_employee_categories,
  } = useSelector(
    (state) => ({
      fetched_scheduled_task: state.task_templates?.fetched_scheduled_task,
      get_employees: state.employees,
      get_auth: state.auth,
      pendingReport: state.task_templates?.pendingReport,
      updated_email_template: state.email_templates.updated_email_template,
      filters: state.audit?.new_filters,
      get_employee_categories: state.employee_category,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (fetched_scheduled_task) {
      const emailTemplate = fetched_scheduled_task?.task_template_email;
      setEmailSubject(emailTemplate?.email_subject || "");
      setEmailBody(emailTemplate?.email_content || "");

      // Update survey info when task is fetched
      setSurveyInfo({
        title: fetched_scheduled_task?.task_template_user_tasks?.name || fetched_scheduled_task?.name || "",
        description: fetched_scheduled_task?.description || "",
        createdBy: fetched_scheduled_task?.created_by?.name || "",
        createdDate: fetched_scheduled_task?.created_at || "",
        updatedDate: fetched_scheduled_task?.updated_at || "",
        surveyIsAnonymous: fetched_scheduled_task?.is_anonymous || false,
        surveyIsOpen: fetched_scheduled_task?.status !== 51,
        task_template_id: fetched_scheduled_task?.task_template_user_tasks?.id || null,
      });
    }
  }, [fetched_scheduled_task]);


  // 1) Add user(s) to Report Access
  const addEmployeeReportAccess = async (task, rawRecipients) => {
    if (!rawRecipients || rawRecipients.length === 0) return;
    try {
      const employees = get_employees?.employees || [];
      const { idList, emailList } = mapRecipientsToApi(rawRecipients, employees);

      if (idList.length === 0 && emailList.length === 0) {
        addToast("No valid user(s) to add.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const payload = {
        task_id: Number(task),
        employee_id_list: idList,
      };

      if (emailList.length > 0) {
        payload.email_list = emailList;
      }

      await dispatch({
        type: SG_ADD_EMPLOYEE_REPORT_ACCESS,
        payload,
      });
      addToast("User(s) successfully added to the report.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("An error occurred while adding user(s) to the report.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  // 2) Remove user(s) from Report Access
  const removeEmployeeReportAccess = async (id, rawRecipients) => {
    if (!rawRecipients || rawRecipients.length === 0) return;
    try {
      const employees = get_employees?.employees || [];
      const { idList } = mapRecipientsToApi(rawRecipients, employees);
      if (idList.length === 0) {
        addToast("No valid user(s) to remove.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      await dispatch({
        type: SG_REMOVE_EMPLOYEE_REPORT_ACCESS,
        payload: {
          task_id: Number(id),
          employee_id_list: idList,
        },
      });
      addToast("User access removed from the report.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("An error occurred while removing user access.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const saveChangesAndAssignUsers = async (templateId, employeesList) => {
    try {
      let emailTaskId = fetched_scheduled_task?.task_template_email?.id;

      if (hasEmailChanged) {
        const emailPayload = {
          email_subject: newEmailSubject,
          email_content: newEmailBody,
          task_template: templateId,
          id: emailTaskId,
        };

        // Update Email Template
        await dispatch({
          type: SG_UPDATE_EMAIL_TEMPLATE,
          payload: emailPayload,
        });
        setPendingUserAssignment({ templateId, employeesList });
        addToast("Updating email template...", {
          appearance: "info",
          autoDismiss: true,
        });
      } else {
        // Directly assign users if no email update is needed
        assignUsersToTask(templateId, employeesList);
      }
    } catch (error) {
      addToast("An error occurred while saving changes.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const assignUsersToTask = async (templateId, employeesList) => {
    try {
      // Use the scheduled task ID (same as addEmployeeReportAccess) instead of template ID
      const scheduledTaskId = fetched_scheduled_task?.id;
      if (!scheduledTaskId) {
        addToast("Scheduled task not found. Cannot assign users.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const userTaskPayload = {
        task_id: scheduledTaskId,
        employee_id_list: employeesList,
      };

      await dispatch({ type: SG_ADD_USER_TASK, payload: userTaskPayload });
      addToast("Users assigned successfully.", {
        appearance: "success",
        autoDismiss: true,
      });
      setPendingUserAssignment(null); // Clear pending assignment
    } catch (error) {
      addToast("An error occurred while assigning users.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  useEffect(() => {
    // Trigger user assignment after email update
    if (updated_email_template && pendingUserAssignment) {
      const { templateId, employeesList } = pendingUserAssignment;
      assignUsersToTask(templateId, employeesList);
    }
  }, [updated_email_template]);

  const handleEmailUpdate = (subject, body) => {
    if (subject !== emailSubject || body !== emailBody) {
      setHasEmailChanged(true);
      setNewEmailSubject(subject);
      setNewEmailBody(body);
    }
  };

  const fetchSurvey = (taskId) => {
    const targetId = Number(taskId ?? numericSurveyId);
    if (!targetId) return;
    // Reset hydration before starting a fresh fetch to avoid UI flicker
    setHasHydrated(false);
    dispatch({ type: SG_FETCH_TASK_SCHEDULE, payload: targetId });
  };

  const refreshReportAccess = () => {
    if (!numericSurveyId) return;
    dispatch({ type: SG_FETCH_TASK_SCHEDULE, payload: numericSurveyId });
  };

  const deleteEmployeeFromSurvey = (templateId, employee) => {
    if (employee) {
      const structurePayload = {
        task_id: Number(templateId),
        employee_id_list: [employee],
      };
      dispatch({ type: SG_DELETE_USER_TASK, payload: structurePayload });

      // Update the survey participants
      setSurveyParticipants(surveyParticipants.filter(participant => participant.id !== employee));

      // Add toast message
      addToast("Employee removed from survey", {
        appearance: "success",
        autoDismiss: true,
      });
    } else {
      addToast("Please select an employee to remove", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const handleArchiveSurvey = (templateId) => {
    dispatch({ type: SG_UPDATE_TASK_TEMPLATE, payload: { id: templateId, status: 99 } });
    setSurveyInfo({
      ...surveyInfo,
      surveyIsOpen: false,
    });
    addToast("Survey archived", {
      appearance: "success",
      autoDismiss: true,
    });
  }

  const handleCloseSurvey = (templateId) => {
    dispatch({ type: SG_UPDATE_PULSE, payload: { id: templateId, status: 51 } });
    setSurveyInfo({
      ...surveyInfo,
      surveyIsOpen: false,
    });
    addToast("Survey closed", {
      appearance: "success",
      autoDismiss: true,
    });
  };


  // Process the responses once the scheduled_task is fetched
  useEffect(() => {
    if (!fetched_scheduled_task || !get_employees?.employees) {
      return;
    }

    const scheduleId = Number(fetched_scheduled_task?.id);
    if (numericSurveyId && scheduleId && scheduleId !== numericSurveyId) {
      return;
    }

    const recent = getRecentResponses(
      fetched_scheduled_task,
      get_employees,
      filters,
      get_employee_categories?.employee_org_category
    );
    const surveyTitle =
      fetched_scheduled_task?.task_template_user_tasks?.name ||
      fetched_scheduled_task?.name ||
      "";
    const createdDate = fetched_scheduled_task?.created_at
      ? new Date(fetched_scheduled_task.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
    const updatedDate = fetched_scheduled_task?.updated_at
      ? new Date(fetched_scheduled_task.updated_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
    const createdBy =
      fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.find(
        (task) => task.task_type === "created_by"
      )?.name;
    const description =
      fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.find(
        (task) => task.task_type === "description"
      )?.text;

    const participants = [
      ...Object.values(fetched_scheduled_task?.user_requests?.user_requests || {}),
    ];

    const missingParticipants =
      fetched_scheduled_task?.assigned_employees?.filter(
        (employee) => !participants.some((participant) => participant.id === employee)
      ) || [];

    const missingParticipantsObjects = (get_employees?.employees || []).filter((employee) =>
      missingParticipants.includes(employee.id)
    );

    missingParticipantsObjects.forEach((employee) => {
      participants.push({
        id: employee.id,
        email: employee.email,
        email_sent: false,
        first_name: employee.first_name,
        status: "Pending",
      });
    });

    const questions =
      fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.filter(
        (task) => task.task_type === "question"
      ) || [];

    const downloadReports = Array.isArray(fetched_scheduled_task?.task_reports)
      ? [...fetched_scheduled_task.task_reports]
      : [];
    if (pendingReport && Object.keys(pendingReport)?.length > 0) {
      downloadReports.push(pendingReport);
    }

    const isAnon =
      fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.find(
        (task) => task.task_type === "survey_is_anonymous"
      )?.value;

    setSurveyIsAnonymous(isAnon);
    setDownloadableReports(downloadReports);
    setQuestions(questions);

    participants.forEach((participant) => {
      const employeeObj = get_employees?.employees?.find(
        (emp) => emp.id === participant.id
      );
      participant.employee_full_name = `${employeeObj?.first_name || ""} ${
        employeeObj?.last_name || ""
      }`.trim();
    });

    setSurveyParticipants(participants);
    setSurveyInfo({
      title: surveyTitle,
      description: description,
      createdBy: createdBy,
      createdDate: createdDate,
      updatedDate: updatedDate,
      surveyIsAnonymous: isAnon,
      surveyIsOpen: fetched_scheduled_task?.status === 1,
      task_template_id: fetched_scheduled_task?.task_template_user_tasks?.id || null,
    });

    setResponses(recent);

    const complianceData = calculateCompliance(participants, recent);
    setCompliance(complianceData);

    setHasHydrated(true);
  }, [fetched_scheduled_task, get_employees, pendingReport, filters, get_employee_categories, numericSurveyId]);

  useEffect(() => {
    if (!fetched_scheduled_task) {
      setReportAccess([]);
      return;
    }

    const scheduleId = Number(fetched_scheduled_task?.id);
    if (numericSurveyId && scheduleId && scheduleId !== numericSurveyId) {
      return;
    }

    const rawAccess =
      fetched_scheduled_task?.report_access ||
      fetched_scheduled_task?.task_template_user_tasks?.report_access ||
      [];

    const accessList = Array.isArray(rawAccess)
      ? rawAccess
      : Object.values(rawAccess || {});

    const accessIds = Array.isArray(fetched_scheduled_task?.employee_access)
      ? fetched_scheduled_task.employee_access
      : [];

    const combinedEntries = [
      ...accessList.map((entry) =>
        typeof entry === "number" || typeof entry === "string"
          ? { employee_id: entry }
          : entry
      ),
      ...accessIds.map((id) => ({ employee_id: id })),
    ];

    const uniqueEntries = [];
    const seen = new Set();
    combinedEntries.forEach((entry, index) => {
      const key =
        entry?.employee_id != null
          ? `id-${entry.employee_id}`
          : entry?.email
          ? `email-${normalizeEmail(entry.email)}`
          : `idx-${index}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueEntries.push(entry);
      }
    });

    const employees = get_employees?.employees || [];
    const { byId, byEmail } = buildEmployeeIndexes(employees);

    const hydrated = uniqueEntries.map((entry) => {
      let id =
        entry?.employee_id != null ? Number(entry.employee_id) : null;
      if (id == null && entry?.employee != null) {
        const possibleId = Number(entry.employee);
        id = Number.isNaN(possibleId) ? null : possibleId;
      }

      let name = entry?.name || "-";
      let email = typeof entry?.email === "string" ? entry.email : "";
      const normalizedEntryEmail = normalizeEmail(email);

      if (id != null && byId.has(id)) {
        const employee = byId.get(id);
        name = `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim() || "-";
        email = employee?.email || email;
      } else if (normalizedEntryEmail && byEmail.has(normalizedEntryEmail)) {
        const employee = byEmail.get(normalizedEntryEmail);
        id = Number(employee?.id ?? id);
        name = `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim() || "-";
        email = employee?.email || email;
      } else if (!name || name.trim().length === 0) {
        name = "-";
      }

      return {
        id,
        name,
        email: email || "-",
        role: entry?.role || "Viewer",
        raw: entry,
      };
    });

    setReportAccess(hydrated);
  }, [fetched_scheduled_task, get_employees, numericSurveyId]);

  // Determine if the current user created this survey
  useEffect(() => {
    if (Number(get_auth?.employee_id) === fetched_scheduled_task?.created_by) {
      setIsSurveyCreator(true);
    }
  }, [fetched_scheduled_task, get_auth]);

  // A callback for selecting a user & navigating to "Individual" tab
  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  // A callback to handle returning to the main survey list
  const goBack = () => {
    navigate("/app/otp/AI/pulse-survey");
  };

  const updateSurveyName = async (newName, taskTemplateId) => {
    try {
      // Update the name of the task template within the user_tasks array
      const updatedUserTasks = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.map(task => {
        if (task.task_type === "name") {
          return { ...task, text: newName };
        }
        return task;
      });
      const payload = {
        id: taskTemplateId,
        name: newName,
        task_template_user_tasks: {
          ...fetched_scheduled_task?.task_template_user_tasks,
          user_tasks: updatedUserTasks
        }
      };

      await dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payload
      });
      setSurveyInfo(prev => ({
        ...prev,
        title: newName
      }));
      addToast("Survey name updated successfully", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("Failed to update survey name", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const updateSurveyDescription = async (newDescription, taskTemplateId) => {
    try {
      // Description is found in the user_tasks array
      const descriptionTask = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.find(task => task.task_type === "description");
      if (!descriptionTask) {
        throw new Error("Description task not found");
      }
      // Update the text property of the description task
      descriptionTask.text = newDescription;

      // Create the payload, Preserve the other user_tasks, only update the description, leave the rest of the user_tasks the same
      // Loop through the user_tasks array and create a new array with the updated description
      const updatedUserTasks = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.map(task => {
        if (task.task_type === "description") {
          return { ...task, text: newDescription };
        }
        return task;
      });
      const payload = {
        id: taskTemplateId,
        task_template_user_tasks: {
          ...fetched_scheduled_task?.task_template_user_tasks,
          user_tasks: updatedUserTasks
        }
      };
      await dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payload
      });
      setSurveyInfo(prev => ({
        ...prev,
        description: newDescription
      }));
      addToast("Survey description updated successfully", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("Failed to update survey description", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  // We'll expose these as context values
  return (
    <SurveyDataContext.Provider
      value={{
        id: resolvedSurveyId,
        fetched_scheduled_task,
        get_employees,
        hasHydrated,
        responses,
        selectedUser,
        setSelectedUser,
        handleUserSelect,
        isSurveyCreator,
        goBack,
        fetchSurvey,
        surveyInfo,
        surveyParticipants,
        questions,
        downloadableReports,
        reportAccess,
        deleteEmployeeFromSurvey,
        surveyIsAnonymous,
        handleEmailUpdate,
        emailSubject,
        emailBody,
        saveChangesAndAssignUsers,
        addEmployeeReportAccess,
        removeEmployeeReportAccess,
        refreshReportAccess,
        handleCloseSurvey,
        updateSurveyName,
        updateSurveyDescription,
        handleArchiveSurvey,
        compliance,
      }}
    >
      {children}
    </SurveyDataContext.Provider>
  );
};
